import { z } from 'zod';
import type { LLMHostConfig } from './llmHost/llmHostTypes.js';
import type { SnapshotSanitizer } from '../assertions/validators/types.js';

// Re-export sanitizer types from canonical source (validators/types.ts)
// Note: For JSON datasets, the Zod schema below validates that patterns are strings.
// The TypeScript types allow RegExp for runtime usage with Playwright matchers.
export type {
  BuiltInSanitizer,
  SnapshotSanitizer,
  RegexSanitizer,
  FieldRemovalSanitizer,
} from '../assertions/validators/types.js';

/**
 * Evaluation mode
 */
export type EvalMode = 'direct' | 'llm_host';

/**
 * A single eval test case
 *
 * For 'direct' mode: toolName and args are required
 * For 'llm_host' mode: scenario and llmHostConfig are required
 */
export interface EvalCase {
  /**
   * Unique identifier for this test case
   */
  id: string;

  /**
   * Human-readable description of what this test case validates
   */
  description?: string;

  /**
   * Evaluation mode
   * - 'direct': Direct API calls to MCP tools (default)
   * - 'llm_host': LLM-driven tool selection via natural language
   *
   * @default 'direct'
   */
  mode?: EvalMode;

  /**
   * Name of the MCP tool to call (required for 'direct' mode, optional for 'llm_host' mode)
   */
  toolName?: string;

  /**
   * Arguments to pass to the tool (required for 'direct' mode, optional for 'llm_host' mode)
   */
  args?: Record<string, unknown>;

  /**
   * Natural language scenario for LLM to execute (optional, required for 'llm_host' mode)
   *
   * @example "Get the weather for London and tell me if I need an umbrella"
   */
  scenario?: string;

  /**
   * LLM host configuration (optional for 'llm_host' mode)
   *
   * If not specified, uses default configuration from test environment
   */
  llmHostConfig?: LLMHostConfig;

  /**
   * Additional metadata for this test case
   *
   * For 'llm_host' mode, can include 'expectedToolCalls' for validation
   */
  metadata?: Record<string, unknown>;

  /**
   * Expectations to validate against the tool response
   *
   * Multiple expectations can be combined and will all be validated.
   *
   * @example
   * ```json
   * {
   *   "id": "weather-london",
   *   "toolName": "get_weather",
   *   "args": { "city": "London" },
   *   "expect": {
   *     "containsText": ["temperature", "conditions"],
   *     "schema": "WeatherResponse",
   *     "responseSize": { "maxBytes": 10000 },
   *     "isError": false
   *   }
   * }
   * ```
   */
  expect?: EvalExpectBlock;
}

/**
 * Unified expectation block for eval cases
 *
 * Mirrors the Playwright matcher API for consistency.
 */
export interface EvalExpectBlock {
  /**
   * Exact response match (toMatchToolResponse)
   */
  response?: unknown;

  /**
   * Name of schema to validate against (toMatchToolSchema)
   */
  schema?: string;

  /**
   * Text substring(s) that must be present (toContainToolText)
   */
  containsText?: string | string[];

  /**
   * Regex pattern(s) that must match (toMatchToolPattern)
   */
  matchesPattern?: string | string[];

  /**
   * Snapshot name for comparison (toMatchToolSnapshot)
   */
  snapshot?: string;

  /**
   * Snapshot sanitizers to apply
   */
  snapshotSanitizers?: SnapshotSanitizer[];

  /**
   * Error expectation (toBeToolError)
   * - true: expects any error
   * - false: expects no error
   * - string: expects error containing this message
   */
  isError?: boolean | string | string[];

  /**
   * LLM-as-judge evaluation (toPassToolJudge)
   */
  passesJudge?: {
    /** Evaluation rubric/criteria */
    rubric: string;
    /** Reference response to compare against */
    reference?: unknown;
    /** Score threshold for passing (0-1, default: 0.7) */
    threshold?: number;
    /** Judge configuration ID */
    configId?: string;
  };

  /**
   * Response size validation (toHaveToolResponseSize)
   */
  responseSize?: {
    /** Maximum allowed size in bytes */
    maxBytes?: number;
    /** Minimum required size in bytes */
    minBytes?: number;
  };
}

/**
 * A complete eval dataset containing multiple test cases
 */
export interface EvalDataset {
  /**
   * Dataset name
   */
  name: string;

  /**
   * Dataset description
   */
  description?: string;

  /**
   * Test cases in this dataset
   */
  cases: Array<EvalCase>;

  /**
   * Optional schema definitions referenced by test cases
   */
  schemas?: Record<string, z.ZodSchema>;

  /**
   * Additional dataset metadata
   */
  metadata?: Record<string, unknown>;
}

/**
 * Zod schema for LLMHostConfig (simplified for serialization)
 */
const LLMHostConfigSchema = z.object({
  provider: z.enum(['openai', 'anthropic']),
  apiKeyEnvVar: z.string().optional(),
  model: z.string().optional(),
  maxTokens: z.number().optional(),
  temperature: z.number().optional(),
  maxToolCalls: z.number().optional(),
});

/**
 * Zod schema for SnapshotSanitizer
 */
const SnapshotSanitizerSchema = z.union([
  // Built-in sanitizers
  z.enum(['timestamp', 'uuid', 'iso-date', 'objectId', 'jwt']),
  // Custom regex sanitizer
  z.object({
    pattern: z.string(),
    replacement: z.string().optional(),
  }),
  // Field removal sanitizer
  z.object({
    remove: z.array(z.string()),
  }),
]);

/**
 * Zod schema for EvalExpectBlock
 */
const EvalExpectBlockSchema = z.object({
  response: z.unknown().optional(),
  schema: z.string().optional(),
  containsText: z.union([z.string(), z.array(z.string())]).optional(),
  matchesPattern: z.union([z.string(), z.array(z.string())]).optional(),
  snapshot: z.string().optional(),
  snapshotSanitizers: z.array(SnapshotSanitizerSchema).optional(),
  isError: z.union([z.boolean(), z.string(), z.array(z.string())]).optional(),
  passesJudge: z
    .object({
      rubric: z.string(),
      reference: z.unknown().optional(),
      threshold: z.number().min(0).max(1).optional(),
      configId: z.string().optional(),
    })
    .optional(),
  responseSize: z
    .object({
      maxBytes: z.number().optional(),
      minBytes: z.number().optional(),
    })
    .optional(),
});

/**
 * Zod schema for EvalCase
 *
 * toolName and args are optional for llm_host mode (which uses scenario instead)
 */
export const EvalCaseSchema = z.object({
  id: z.string().min(1, 'id must not be empty'),
  description: z.string().optional(),
  mode: z.enum(['direct', 'llm_host']).optional(),
  toolName: z.string().min(1, 'toolName must not be empty').optional(),
  args: z.record(z.unknown()).optional(),
  scenario: z.string().optional(),
  llmHostConfig: LLMHostConfigSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
  expect: EvalExpectBlockSchema.optional(),
});

/**
 * Zod schema for EvalDataset (without schemas field, as schemas aren't serializable)
 */
export const EvalDatasetSchema = z.object({
  name: z.string().min(1, 'name must not be empty'),
  description: z.string().optional(),
  cases: z.array(EvalCaseSchema).min(1, 'dataset must have at least one case'),
  metadata: z.record(z.unknown()).optional(),
});

/**
 * Type for serialized eval dataset (without Zod schemas)
 */
export type SerializedEvalDataset = z.infer<typeof EvalDatasetSchema>;

/**
 * Validates an eval case
 *
 * @param evalCase - The eval case to validate
 * @returns The validated eval case
 * @throws {z.ZodError} If validation fails
 */
export function validateEvalCase(evalCase: unknown): EvalCase {
  return EvalCaseSchema.parse(evalCase);
}

/**
 * Validates a serialized eval dataset
 *
 * @param dataset - The dataset to validate
 * @returns The validated dataset
 * @throws {z.ZodError} If validation fails
 */
export function validateEvalDataset(dataset: unknown): SerializedEvalDataset {
  return EvalDatasetSchema.parse(dataset);
}
